// src/pages/reports/qrPaymentReport/QrPaymentReport.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCompanyList } from 'redux/actions/companyAction';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import { getQrPaymentReports, postQrPaymentReport } from 'redux/actions/QrPaymentReportAction';
import CommonDateRangePicker from 'components/commonDateRangePicker/CommonDateRangePicker';
import BillingFilter from 'pages/operation/billing/billingFlow/billingFilter/BillingFilter';
import { getDateFormat } from 'utils/utils';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// ─── Status chip colours ─────────────────────────────────────────────────────

const STATUS_COLORS = {
  // Trip statuses
  COMPLETED: { bg: '#e8f5e9', color: '#2e7d32' },
  CANCELLED: { bg: '#fce4ec', color: '#c62828' },
  IN_PROGRESS: { bg: '#fff3e0', color: '#e65100' },
  // Payment / settlement statuses
  SUCCESS: { bg: '#e8f5e9', color: '#2e7d32' },
  FAILED: { bg: '#fce4ec', color: '#c62828' },
  PENDING: { bg: '#fff3e0', color: '#e65100' },
  SETTLED: { bg: '#e3f2fd', color: '#0d47a1' },
  UNSETTLED: { bg: '#fafafa', color: '#616161' },
};

const StatusChip = ({ label }) => {
  if (!label) return <span style={{ color: '#aaa' }}>—</span>;
  const upper = String(label).toUpperCase().replace(/\s+/g, '_');
  const style = STATUS_COLORS[upper] || { bg: '#f5f5f5', color: '#424242' };
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '11px',
        height: 22,
      }}
    />
  );
};

// ─── Helper: build filter options ────────────────────────────────────────────

const filterOptions = (companyList, cityList) => [
  { id: 1, name: 'city', placeholder: 'All City', options: cityList },
  {
    id: 2,
    name: 'companyName',
    placeholder: 'All Company Name',
    options: companyList,
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function QrPaymentReport() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ── date range (last 30 days default) ─────────────────────────────────────
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(getDateFormat(thirtyDaysAgo, 'reservationDateFormat'));
  const [dateTo, setDateTo] = useState(getDateFormat(new Date(), 'reservationDateFormat'));

  // ── filter dropdowns ──────────────────────────────────────────────────────
  const [billingFilter, setBillingFilter] = useState({
    companyName: {},
    city: {},
  });

  // ── table state ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // loading / generating / emailing — driven by Redux; local flags kept for
  // backward compat with the UI but bound to Redux state below.
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // ── redux data ────────────────────────────────────────────────────────────
  const companyTableData = useSelector((s) => s?.userCompany?.companyList?.body);
  const locationOptions = useSelector((s) => s?.userServiceCity?.userServiceCity?.data);
  // QR report list from Redux
  const qrReportState = useSelector((s) => s?.getQrPaymentReportList);
  const reportLoading = qrReportState?.loading || false;
  const qrReportData = qrReportState?.qrPaymentList;
  // Export/email loading state
  const exportLoading = useSelector((s) => s?.postQrPaymentReport?.loading) || false;

  // Derive table data from Redux state
  const tableData = {
    header: qrReportData?.data?.header || [],
    body: qrReportData?.data?.body || [],
    totalCount: qrReportData?.data?.totalCount || 0,
  };
  const loading = reportLoading;
  const generating = exportLoading;
  const emailing = exportLoading;

  const companyOption =
    companyTableData?.length > 0
      ? companyTableData.map((v) => ({ value: v?.id, label: v.name }))
      : [];

  const cityOption =
    locationOptions?.length > 0
      ? locationOptions.map((v) => ({ value: v?.id, label: v.name }))
      : [];

  // ── initial data load ─────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(
      fetchCompanyList({ search: '' }, { searchType: 'globalSearch', loader: 'backDropLoader' }),
    );
    dispatch(getServiceCity());
  }, []);

  // ── build current filters ─────────────────────────────────────────────────
  const buildFilters = useCallback(
    () => ({
      page,
      pageSize: rowsPerPage,
      dateFrom,
      dateTo,
      city: billingFilter?.city?.value || '',
      companyId: billingFilter?.companyName?.value || '',
    }),
    [page, rowsPerPage, dateFrom, dateTo, billingFilter],
  );

  // ── fetch report list via Redux ───────────────────────────────────────────
  const fetchReport = useCallback(
    (overrides = {}) => {
      const filters = { ...buildFilters(), ...overrides };
      dispatch(getQrPaymentReports(filters));
    },
    [dispatch, buildFilters],
  );

  useEffect(() => {
    fetchReport();
  }, [page, rowsPerPage]);

  // ── date filter ───────────────────────────────────────────────────────────
  const onSelectDateRange = (newValue) => {
    setPage(0);
    let from = '',
      to = '';
    if (newValue && newValue !== 0) {
      from = getDateFormat(newValue[0], 'reservationDateFormat');
      to = getDateFormat(newValue[1], 'reservationDateFormat');
    }
    setDateFrom(from);
    setDateTo(to);
    fetchReport({
      page: 0,
      dateFrom: from,
      dateTo: to,
      city: billingFilter?.city?.value || '',
      companyId: billingFilter?.companyName?.value || '',
    });
  };

  // ── dropdown filter ───────────────────────────────────────────────────────
  const handleFilter = (name, newValue) => {
    const updated = { ...billingFilter, [name]: newValue };
    setBillingFilter(updated);
    fetchReport({
      page: 0,
      city: name === 'city' ? newValue?.value : billingFilter?.city?.value,
      companyId: name === 'companyName' ? newValue?.value : billingFilter?.companyName?.value,
    });
    setPage(0);
  };

  // ── company autocomplete ──────────────────────────────────────────────────
  const onHandleAutoSuggestion = debounce((event, value, fieldName) => {
    if (event?.type === 'change' && fieldName === 'companyName') {
      dispatch(
        fetchCompanyList(
          { search: value || '' },
          { searchType: 'globalSearch', loader: 'backDropLoader' },
        ),
      );
    }
  }, 500);

  // ── pagination ────────────────────────────────────────────────────────────
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangePerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ── report generation (dispatched via Redux) ──────────────────────────────
  const handleRequestReport = (format) => {
    setAnchorEl(null);
    dispatch(
      postQrPaymentReport({
        startDate: dateFrom,
        endDate: dateTo,
        format,
        filters: {
          city: billingFilter?.city?.value || '',
          companyId: billingFilter?.companyName?.value || '',
        },
        notifyOnComplete: false,
        navigate,
      }),
    );
  };

  const handleSendToEmail = () => {
    dispatch(
      postQrPaymentReport({
        startDate: dateFrom,
        endDate: dateTo,
        format: 'csv',
        filters: {
          city: billingFilter?.city?.value || '',
          companyId: billingFilter?.companyName?.value || '',
        },
        notifyOnComplete: true,
      }),
    );
  };

  // Internal IDs (tripId, vehicleId) are noise for finance users — keep them
  // in the API payload but hide the columns (iisc-dev PR #127).
  const filteredHeaders = tableData.header.filter(
    (col) => !['tripId', 'vehicleId'].includes(col.key),
  );

  // ─── Date/time formatting (PR #127) ───
  const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value; // not parseable — show raw value rather than hide it
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const DATE_TIME_KEYS = [
    'scheduledTime',
    'actualPickupTime',
    'actualDropTime',
    'createdOn',
    'lastUpdated',
    'completedOn',
    'invoiceDate',
    'settlementDate',
    'refundDate',
  ];

  const isDateTimeColumn = (key = '') => DATE_TIME_KEYS.includes(key);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Page heading ───────────────────────────────────────────────────── */}
      <Box className="justifyBetween pb10">
        <Typography variant="body1" className="colorPrimary">
          QR Payment Operations
        </Typography>
      </Box>

      {/* ── Filters row ────────────────────────────────────────────────────── */}
      <Grid
        container
        gap={2}
        sx={{ width: '100%' }}
        flexWrap={{ xs: 'wrap', md: 'nowrap' }}
        direction="row"
        alignItems="center"
      >
        {/* Dropdowns */}
        <Grid item container sx={{ width: '100%', ml: '10px' }} gap={2}>
          {filterOptions(companyOption, cityOption).map((formData, i) => (
            <Grid item lg={5} md={12} xs={12} key={i} className="misAutoComplete">
              <BillingFilter
                billingFilterValue={billingFilter}
                setBillingFilter={setBillingFilter}
                handleFilter={handleFilter}
                formData={formData}
                onHandleAutoSuggestion={onHandleAutoSuggestion}
              />
            </Grid>
          ))}
        </Grid>

        {/* Date + action buttons */}
        <Grid container item>
          <Grid
            item
            lg={12}
            xs={12}
            className="justifyEnd"
            style={{ marginTop: '24px', marginLeft: '10px' }}
          >
            <CommonDateRangePicker
              onSelectDateRange={onSelectDateRange}
              dateFrom={dateFrom}
              dateTo={dateTo}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'flex-end' }}>
            {/* Request Report → dropdown (CSV / PDF) */}
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              disabled={generating}
              // startIcon={
              //   generating ? (
              //     <CircularProgress size={16} sx={{ color: "#fff" }} />
              //   ) : (
              //     <DownloadIcon />
              //   )
              // }
              sx={{
                backgroundColor: '#1976d2',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '8px',
                px: 2,
                py: 1,
                '&:hover': { backgroundColor: '#1565c0' },
                '&.Mui-disabled': { backgroundColor: '#9e9e9e', color: '#fff' },
              }}
            >
              {generating ? 'Requesting…' : 'Request Report'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: { width: anchorEl ? anchorEl.offsetWidth : 'auto' },
              }}
            >
              <MenuItem onClick={() => handleRequestReport('csv')} sx={{ fontSize: '14px' }}>
                Excel / CSV
              </MenuItem>
              <MenuItem onClick={() => handleRequestReport('pdf')} sx={{ fontSize: '14px' }}>
                PDF
              </MenuItem>
            </Menu>

            {/* Send to email */}
            <Button
              onClick={handleSendToEmail}
              disabled={emailing}
              // startIcon={
              //   emailing ? (
              //     <CircularProgress size={16} sx={{ color: "#fff" }} />
              //   ) : (
              //     <EmailIcon />
              //   )
              // }
              sx={{
                backgroundColor: '#f59e0b',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '8px',
                px: 2,
                py: 1,
                '&:hover': { backgroundColor: '#d97706' },
                '&.Mui-disabled': { backgroundColor: '#9e9e9e', color: '#fff' },
              }}
            >
              {emailing ? 'Sending…' : 'Send Report to Mail'}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          maxHeight: '70vh',
          overflowY: 'auto',
          ml: '10px',
          mt: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tableData.body.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#aaa' }}>
            <Typography variant="body2">
              No QR payment transactions found for the selected filters.
            </Typography>
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1200,
                  '& .MuiTableCell-root': {
                    fontFamily: 'Poppins, sans-serif',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {filteredHeaders.map((col) => (
                      <TableCell
                        key={col.key}
                        align="left"
                        sx={{
                          backgroundColor: 'var(--brand-primary)',
                          color: '#FFFFFF',
                          fontSize: '16px',
                          fontWeight: 500,
                          fontFamily: 'Poppins, sans-serif',
                          // whiteSpace: "nowrap",
                          padding: '10px 16px',
                          borderBottom: 'none',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.body.map((row, rIdx) => (
                    <TableRow
                      key={rIdx}
                      hover
                      sx={{
                        backgroundColor: '#fff',
                        '&:hover': {
                          backgroundColor: '#f8fbff',
                        },
                        '& td': {
                          borderBottom: '1px solid #ECECEC',
                        },
                      }}
                    >
                      {filteredHeaders.map((col) => {
                        const val = isDateTimeColumn(col.key)
                          ? formatDateTime(row[col.key])
                          : row[col.key];
                        const isStatus =
                          col.key.toLowerCase().includes('status') ||
                          col.key.toLowerCase().includes('payment') ||
                          col.key.toLowerCase().includes('settlement');
                        return (
                          <TableCell
                            key={col.key}
                            sx={{
                              fontSize: '16px',
                              fontWeight: 400,
                              color: '#000000DE',
                              fontFamily: 'Poppins, sans-serif',
                              padding: '18px 18px',
                              letterSpacing: '0.01071em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {isStatus ? (
                              <StatusChip label={val} />
                            ) : (
                              <Tooltip title={val ?? ''} placement="top">
                                <span>{val ?? '—'}</span>
                              </Tooltip>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* <TablePagination
              component="div"
              count={tableData.totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangePerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: "1px solid #e0e0e0" }}
            /> */}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
